import { memo } from "react";

export type ShapeOption = {
  value: string;
  label: string;
  category?: "basic" | "process" | "mindmap" | "other";
};

const SHAPES: ShapeOption[] = [
  // Basic shapes
  { value: "rectangle", label: "Rechteck", category: "basic" },
  { value: "circle", label: "Kreis", category: "basic" },
  { value: "ellipse", label: "Ellipse", category: "basic" },
  { value: "pill", label: "Pill", category: "basic" },
  { value: "diamond", label: "Raute", category: "basic" },

  // Process shapes
  { value: "parallelogram", label: "Parallelogramm", category: "process" },
  { value: "hexagon", label: "Hexagon", category: "process" },
  { value: "trapezoid", label: "Trapez", category: "process" },
  { value: "triangle", label: "Dreieck", category: "process" },
  { value: "cylinder", label: "Zylinder", category: "process" },

  // Mindmap/Creative shapes
  { value: "cloud", label: "Wolke", category: "mindmap" },
  { value: "star", label: "Stern", category: "mindmap" },
  { value: "document", label: "Dokument", category: "other" },
];

type ShapePickerProps = {
  value: string | null | undefined;
  onChange: (shape: string) => void;
  disabled?: boolean;
  compact?: boolean;
};

/**
 * Visual shape picker component with preview icons
 */
export const ShapePicker = memo(({ value, onChange, disabled, compact }: ShapePickerProps) => {
  const selectedShape = value || "rectangle";

  // Render SVG preview for each shape
  const renderShapePreview = (shape: string) => {
    const size = compact ? 32 : 48;
    const strokeWidth = compact ? 1.5 : 2;

    switch (shape) {
      case "rectangle":
        return (
          <svg width={size} height={size} viewBox="0 0 48 48">
            <rect x="8" y="12" width="32" height="24" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth={strokeWidth} />
          </svg>
        );

      case "circle":
        return (
          <svg width={size} height={size} viewBox="0 0 48 48">
            <circle cx="24" cy="24" r="16" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth={strokeWidth} />
          </svg>
        );

      case "ellipse":
        return (
          <svg width={size} height={size} viewBox="0 0 48 48">
            <ellipse cx="24" cy="24" rx="18" ry="12" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth={strokeWidth} />
          </svg>
        );

      case "pill":
        return (
          <svg width={size} height={size} viewBox="0 0 48 48">
            <rect x="8" y="16" width="32" height="16" rx="8" ry="8" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth={strokeWidth} />
          </svg>
        );

      case "diamond":
        return (
          <svg width={size} height={size} viewBox="0 0 48 48">
            <polygon points="24,8 40,24 24,40 8,24" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth={strokeWidth} />
          </svg>
        );

      case "parallelogram":
        return (
          <svg width={size} height={size} viewBox="0 0 48 48">
            <polygon points="12,12 38,12 36,36 10,36" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth={strokeWidth} />
          </svg>
        );

      case "hexagon":
        return (
          <svg width={size} height={size} viewBox="0 0 48 48">
            <polygon points="24,8 38,16 38,32 24,40 10,32 10,16" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth={strokeWidth} />
          </svg>
        );

      case "trapezoid":
        return (
          <svg width={size} height={size} viewBox="0 0 48 48">
            <polygon points="10,12 38,12 34,36 14,36" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth={strokeWidth} />
          </svg>
        );

      case "triangle":
        return (
          <svg width={size} height={size} viewBox="0 0 48 48">
            <polygon points="24,10 38,36 10,36" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth={strokeWidth} />
          </svg>
        );

      case "cloud":
        return (
          <svg width={size} height={size} viewBox="0 0 48 48">
            <path
              d="M 14,18 C 10,18 7,21 7,25 C 7,27 8,29 10,30 L 38,30 C 40,29 41,27 41,25 C 41,22 39,19 36,19 C 36,14 32,10 27,10 C 23,10 20,12 19,15 C 18,13 16,12 14,12 C 12,12 10,13 10,15 Z"
              fill="currentColor"
              fillOpacity="0.15"
              stroke="currentColor"
              strokeWidth={strokeWidth}
            />
          </svg>
        );

      case "star":
        return (
          <svg width={size} height={size} viewBox="0 0 48 48">
            <polygon points="24,8 28,18 38,18 30,24 33,36 24,30 15,36 18,24 10,18 20,18" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth={strokeWidth} />
          </svg>
        );

      case "cylinder":
        return (
          <svg width={size} height={size} viewBox="0 0 48 48">
            <ellipse cx="24" cy="14" rx="12" ry="4" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth={strokeWidth} />
            <rect x="12" y="14" width="24" height="20" fill="currentColor" fillOpacity="0.15" stroke="none" />
            <line x1="12" y1="14" x2="12" y2="34" stroke="currentColor" strokeWidth={strokeWidth} />
            <line x1="36" y1="14" x2="36" y2="34" stroke="currentColor" strokeWidth={strokeWidth} />
            <ellipse cx="24" cy="34" rx="12" ry="4" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth={strokeWidth} />
          </svg>
        );

      case "document":
        return (
          <svg width={size} height={size} viewBox="0 0 48 48">
            <path
              d="M 12,8 L 36,8 L 36,32 Q 30,36 24,32 Q 18,28 12,32 Z"
              fill="currentColor"
              fillOpacity="0.15"
              stroke="currentColor"
              strokeWidth={strokeWidth}
            />
          </svg>
        );

      default:
        return (
          <svg width={size} height={size} viewBox="0 0 48 48">
            <rect x="8" y="12" width="32" height="24" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth={strokeWidth} />
          </svg>
        );
    }
  };

  if (compact) {
    // Compact mode: Dropdown-style selector
    return (
      <select
        value={selectedShape}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2 text-sm"
      >
        {SHAPES.map((shape) => (
          <option key={shape.value} value={shape.value}>
            {shape.label}
          </option>
        ))}
      </select>
    );
  }

  // Full mode: Visual grid selector
  return (
    <div className="grid grid-cols-4 gap-2">
      {SHAPES.map((shape) => {
        const isSelected = selectedShape === shape.value;
        return (
          <button
            key={shape.value}
            type="button"
            onClick={() => onChange(shape.value)}
            disabled={disabled}
            className={`
              flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all
              ${isSelected
                ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30"
                : "border-[var(--color-border)] bg-white dark:bg-slate-800 hover:border-blue-300 dark:hover:border-blue-700"
              }
              ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
            `}
            title={shape.label}
          >
            <div className={isSelected ? "text-blue-500" : "text-gray-600 dark:text-gray-400"}>
              {renderShapePreview(shape.value)}
            </div>
            <span className="text-xs text-center leading-tight">{shape.label}</span>
          </button>
        );
      })}
    </div>
  );
});

ShapePicker.displayName = "ShapePicker";

export { SHAPES };
